import { useEffect, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import _get from 'lodash/get';
import { trackSeriesResourceViewed } from 'containers/Series/utils';
import Mixpanel from 'utils/mixpanelService';
import {
  getActiveCourseModuleIdx,
  getActiveTab,
  getImmediateNextIndexes,
  getInitialCourseState,
  getInitialPathname,
  getModuleByMatchPath,
  getUncompletedIndexes,
  hasModuleStarted,
  mapSeriesResource,
} from '../utils';

export const useCourseNavigation = ({
  item,
  takenCourses,
  takenCoursesProcessing,
  isEnrolled,
  trackingPath,
}) => {
  const history = useHistory();
  const match = useRouteMatch();

  const [activeModuleIdx, setActiveModuleIdx] = useState(0);
  const [activeSeriesIdx, setActiveSeriesIdx] = useState(0);
  const [activeResourceIdx, setActiveResourceIdx] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const courseModules = _get(item, ['fields', 'modules']);
  const isLastModule = activeModuleIdx + 1 === courseModules?.length;

  const activeModule = _get(item, ['fields', 'modules', activeModuleIdx]);
  const activeSeries = _get(activeModule, [
    'fields',
    'moduleResources',
    activeSeriesIdx,
  ]);
  const activeSeriesResources = useMemo(
    () => _get(activeSeries, 'fields.resources', []).map(mapSeriesResource),
    [activeSeries],
  );
  const activeResource = _get(activeSeriesResources, activeResourceIdx);

  const takenCourse = _get(takenCourses, [_get(item, 'sys.id')], {});
  const completedModulesIds = _get(takenCourse, 'completedModulesIds', []);

  const continueCourse = () => {
    const taken = takenCourses[item.sys.id];
    const moduleIdx = getActiveCourseModuleIdx(item, taken);
    initCourse(moduleIdx);
  };

  const initCourse = moduleIdx => {
    const courseModule = _get(item, ['fields', 'modules', moduleIdx]);
    const taken = takenCourses[item.sys.id];
    const { resourceIdx, seriesIdx } = getInitialCourseState(
      taken,
      courseModule,
    );
    initCourseState({ moduleIdx, seriesIdx, resourceIdx });
  };

  const initCourseState = ({ moduleIdx, seriesIdx, resourceIdx }) => {
    const courseModule = _get(item, ['fields', 'modules', moduleIdx]);
    const series = _get(courseModule, ['fields', 'moduleResources', seriesIdx]);
    const resource = _get(series, ['fields', 'resources', resourceIdx]);
    setActiveModuleIdx(moduleIdx);
    setActiveSeriesIdx(seriesIdx);
    setActiveResourceIdx(resourceIdx);
    setActiveTab(getActiveTab(series, resource));
    history.push(`${courseModule.fields.slug}-module`);
  };

  const onModuleClick = moduleIdx => {
    initCourseState({ moduleIdx, seriesIdx: 0, resourceIdx: 0 });
  };

  const goToResource = data => {
    const { moduleIdx, seriesIdx, resourceIdx } = data;
    initCourseState({ moduleIdx, seriesIdx, resourceIdx });
  };

  const getNextResourceIndexes = props => {
    const mergedProps = {
      modules: courseModules,
      completedModulesIds,
      completedSeriesIds: _get(takenCourse, 'completedSeriesIds', []),
      seriesProgress: _get(takenCourse, 'seriesProgress', {}),
      ...props,
    };

    const uncompletedIndexes = getUncompletedIndexes(mergedProps);

    if (uncompletedIndexes.uncompletedResourceIdx !== -1) {
      return {
        resourceIdx: uncompletedIndexes.uncompletedResourceIdx,
        seriesIdx: uncompletedIndexes.uncompletedSeriesIdx,
        moduleIdx: uncompletedIndexes.uncompletedModuleIdx,
      };
    }

    return getImmediateNextIndexes({
      modules: courseModules,
      series: _get(activeModule, ['fields', 'moduleResources'], []),
      resources: activeSeriesResources,
      moduleIdx: activeModuleIdx,
      seriesIdx: activeSeriesIdx,
      resourceIdx: activeResourceIdx,
    });
  };

  useEffect(() => {
    const type = _get(activeResource, 'sys.contentType.sys.id');
    if (
      history.location.pathname.includes('assessments') &&
      type !== 'assessments'
    ) {
      history.replace(match.url);
    }

    if (activeResource) {
      trackSeriesResourceViewed({
        item: activeSeries,
        resource: activeResource,
        trackingPath,
        module: activeModule.fields.title,
        section: _get(activeSeries, 'fields.title'),
      });
    }
  }, [activeResource]);

  useEffect(() => {
    if (!item || takenCoursesProcessing) return undefined;

    const taken = takenCourses[item.sys.id];
    const pathname = getInitialPathname({ item, taken, isEnrolled, match });
    if (pathname.endsWith('-module')) {
      const { moduleIdx } = getModuleByMatchPath(item, match.params.path);
      initCourse(moduleIdx);
    }
    history.replace(pathname);

    return () => {
      setActiveResourceIdx(null);
      setActiveTab(null);
      setActiveSeriesIdx(0);
      setActiveModuleIdx(0);
    };
  }, [item, takenCoursesProcessing]);

  useEffect(() => {
    if (!item || takenCoursesProcessing) return;

    const taken = takenCourses[item.sys.id];
    const pathname = getInitialPathname({ item, taken, isEnrolled, match });

    if (pathname.endsWith('-module')) {
      const courseModule = _get(item, ['fields', 'modules', activeModuleIdx]);
      const eventName = hasModuleStarted(courseModule, takenCourse)
        ? 'Course Module - Started'
        : 'Course Module - Viewed';
      Mixpanel.track(eventName, {
        course: item.fields.title,
        module: courseModule.fields.title,
        path: trackingPath,
      });
    }
  }, [item, takenCoursesProcessing, match.params.path]);

  return {
    activeModuleIdx,
    activeSeriesIdx,
    activeResourceIdx,
    activeTab,
    activeModule,
    activeSeries,
    activeSeriesResources,
    activeResource,
    setActiveSeriesIdx,
    setActiveResourceIdx,
    setActiveModuleIdx,
    setActiveTab,
    continueCourse,
    onModuleClick,
    getNextResourceIndexes,
    goToResource,
    isLastModule,
  };
};
